/* =============================================================================
   VIEWS / ADMIN-LOGIN — Magic-Link-Login + Logout + Auth-Pille
   =============================================================================
   - doLogin: Supabase Magic Link (einziger unterstützter Auth-Pfad seit dem
     Demo-Code-Entfernen im Audit 2026-05-20 / Finding H-01)
   - doLogout: signOut, View-Reset, Toast
   - renderAuthPill: Status-Pille oben rechts (E-Mail + Logout-Button)
============================================================================ */

import { htmlEscape, showToast } from '../utils/helpers.js';
// S144: Bei einem ECHTEN interaktiven Login den MainUser-Idle-Stempel frisch
// setzen, BEVOR die Admin-Shell den Idle-Boot-Check macht (initAdminShell →
// startMainIdleWatch). Sonst loggt ein stundenalter `gg_main_last_activity`-
// Stempel (z.B. nach Browser-Schließen / abgelaufenem JWT, wo kein sauberes
// auth:logout feuerte und clearLastActivity() nie lief) den User ~1s nach dem
// Login sofort wieder aus → zurück auf die Anmeldemaske, neuer Code/Link nötig.
import { markMainActivity } from '../core/session-idle.js';

export async function doLogin(e) {
  if (e && e.preventDefault) e.preventDefault();
  const App = window.App;
  if (!App || !App.auth) {
    showToast('Auth-Adapter nicht verfügbar', 'error');
    return;
  }
  const btn = e && e.target && e.target.querySelector ? e.target.querySelector('button[type=submit]') : null;

  // Supabase Magic Link + OTP-Code-Hybrid (Demo-Branch wurde mit
  // dem Audit 2026-05-20 / Finding H-01 entfernt).
  // S61 (Tester-Bug-Report info@dpisarev.de): nach erfolgreichem Versand wird
  // zusätzlich Stage 2 eingeblendet — Code-Input-Feld als robuste Alternative
  // zum Klick (Mail-Scanner-immun, Cross-Device-tauglich).
  const email = (document.getElementById('admin-email') || {}).value || '';
  if (!email.trim()) {
    showToast('Bitte E-Mail-Adresse eingeben', 'error');
    return;
  }
  const info = document.getElementById('admin-login-info');
  if (btn) { btn.disabled = true; btn.textContent = 'Sende Link...'; }
  try {
    await App.auth.login({ email });
    if (info) {
      info.innerHTML = _buildMagicLinkInfoHtml(email);
      info.style.display = 'block';
      // Stage 2 — OTP-Code-Input-Feld nach erfolgreichem Versand fokussieren
      const codeInput = document.getElementById('admin-otp-code');
      if (codeInput) {
        // kleine Verzögerung damit Render durch ist
        setTimeout(() => codeInput.focus(), 50);
      }
    }
    if (btn) { btn.disabled = false; btn.textContent = 'Login-Mail senden'; }
    if (window.AppConfig.features.pushNotifications && 'Notification' in window &&
        Notification.permission === 'default') {
      Notification.requestPermission();
    }
  } catch (err) {
    console.error('[doLogin]', err);
    // S144: Supabase-Rate-Limit (over_email_send_rate_limit / HTTP 429) klar
    // benennen statt generischem „Login fehlgeschlagen". In dem Fall wurde
    // vorhin schon eine Mail verschickt — der Code daraus ist noch ~1h gültig,
    // also blenden wir das Code-Feld TROTZDEM ein, damit Jonas den letzten Code
    // eintippen kann, statt frustriert weiter auf „senden" zu drücken.
    const code = (err && (err.code || err.name)) || '';
    const status = err && (err.status || err.statusCode);
    const msg = (err && err.message) ? String(err.message) : String(err);
    const isRateLimit = code === 'over_email_send_rate_limit'
      || status === 429
      || /rate limit|only request this after|after \d+ seconds/i.test(msg);
    if (isRateLimit) {
      const secMatch = msg.match(/after\s+(\d+)\s+second/i);
      const waitHint = secMatch ? `etwa ${secMatch[1]} Sek.` : 'einen Moment';
      // Code-Feld einblenden (vorherige Mail nutzen) statt Sackgasse.
      if (info) {
        info.innerHTML = _buildMagicLinkInfoHtml(email);
        info.style.display = 'block';
        const codeInput = document.getElementById('admin-otp-code');
        if (codeInput) setTimeout(() => codeInput.focus(), 50);
      }
      showToast(
        `Zu viele Login-Mails in kurzer Zeit. Bitte ${waitHint} warten — ` +
        `oder den Code aus der zuletzt erhaltenen Mail unten eingeben (noch ~1 Std. gültig).`,
        'warning'
      );
    } else {
      showToast('Login fehlgeschlagen: ' + msg, 'error');
    }
    if (btn) { btn.disabled = false; btn.textContent = 'Login-Mail senden'; }
  }
}

/**
 * Stage-2 Handler: OTP-Code-Login.
 * Wird vom Code-Input-Form aufgerufen, das nach erfolgreichem doLogin()
 * in #admin-login-info gerendert wird (siehe _buildMagicLinkInfoHtml).
 *
 * S61 Hard-Way-Fix gegen Email-Scanner-Token-Burn und Cross-Device-Bruch.
 */
export async function doVerifyOtpCode(e) {
  if (e && e.preventDefault) e.preventDefault();
  const App = window.App;
  if (!App || !App.auth || typeof App.auth.verifyOtpCode !== 'function') {
    showToast('Auth-Adapter nicht verfügbar', 'error');
    return;
  }
  const form = e && e.target ? e.target : null;
  const btn = form && form.querySelector ? form.querySelector('button[type=submit]') : null;
  const codeInput = document.getElementById('admin-otp-code');
  const email = (document.getElementById('admin-email') || {}).value || '';
  const token = (codeInput && codeInput.value || '').replace(/\s+/g, '');

  if (!email.trim()) {
    showToast('Bitte zuerst E-Mail-Adresse eingeben und Magic Link senden', 'error');
    return;
  }
  if (!/^\d{6,8}$/.test(token)) {
    showToast('Der Login-Code muss aus 6 bis 8 Ziffern bestehen', 'error');
    if (codeInput) codeInput.focus();
    return;
  }
  if (btn) { btn.disabled = true; btn.textContent = 'Prüfe Code...'; }
  try {
    await App.auth.verifyOtpCode({ email, token });
    // S144 Rootcause-Fix: Erfolgreicher Code-Login = frische User-Aktivität.
    // SOFORT stempeln, bevor showAdminPanel()→initAdminShell()→startMainIdleWatch()
    // den Idle-Boot-Check macht. Verhindert den „Login → 1s später Auto-Logout"-
    // Bounce durch einen stale Stempel aus einer vorherigen, nicht sauber
    // beendeten Session.
    markMainActivity();
    // Erfolg — Supabase setzt Session via onAuthStateChange asynchron.
    // Magic-Link-Klick triggert View-Switch automatisch über den Hash-
    // Detect-Reload-Pfad; bei OTP-Code-Login (kein Reload!) müssen wir
    // den View-Switch selbst anstoßen.
    showToast('Login erfolgreich', 'success');

    // 1. Profil aus DB laden, falls onAuthStateChange den User noch nicht
    //    gesetzt hat (Race zwischen verifyOtp-Resolve und Auth-Listener-
    //    Polling). _initFromSession ist idempotent und nimmt entweder
    //    den vom Listener gesetzten _cachedUser oder pollt frisch.
    try {
      if (typeof App.auth._initFromSession === 'function') {
        await App.auth._initFromSession();
      }
    } catch (initErr) {
      console.warn('[doVerifyOtpCode] _initFromSession failed:', initErr);
    }

    // 2. Login-UI aufräumen (Stage-2-Form weg)
    const info = document.getElementById('admin-login-info');
    if (info) { info.style.display = 'none'; info.innerHTML = ''; }
    if (codeInput) codeInput.value = '';

    // 3. URL-Hash auf #admin setzen, damit Reload den User in der
    //    Admin-View hält (analog Magic-Link-Pfad in bootstrap.js).
    if (!window.location.hash.startsWith('#admin')) {
      history.replaceState(null, '', window.location.pathname + '#admin');
    }

    // 4. Hartes View-Switch — bei OTP-Code-Login passiert das sonst nicht
    //    weil kein Page-Reload + kein bootstrap-Phase-Trigger.
    if (typeof window.switchView === 'function') {
      window.switchView('admin');
    }

    // 4b. Login-Card ausblenden + Admin-Panel sichtbar machen + Shell mounten.
    //     Der View-Switch oben zeigt nur den view-admin-Container — INNERHALB
    //     des Containers gibt es zwei Sektionen (admin-login / admin-panel),
    //     der Toggle dazwischen geschieht in showAdminPanel().
    if (typeof window.showAdminPanel === 'function') {
      try { window.showAdminPanel(); } catch (_panelErr) { /* ignore */ }
    }

    // 5. Auth-Pille im Header (Email + Logout-Button) sofort aktualisieren
    if (typeof window.renderAuthPill === 'function') {
      try { await window.renderAuthPill(); } catch (_pillErr) { /* ignore */ }
    }
  } catch (err) {
    console.error('[doVerifyOtpCode]', err);
    const msg = err && err.message ? err.message : String(err);
    // Häufigste Supabase-Fehler abfangen mit klarem Hinweis
    let friendly = msg;
    if (/expired|invalid/i.test(msg)) {
      friendly = 'Code ungültig oder abgelaufen. Bitte „Magic Link senden" erneut anfordern.';
    }
    showToast('Code-Login fehlgeschlagen: ' + friendly, 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'Verifizieren'; }
    if (codeInput) { codeInput.focus(); codeInput.select(); }
    return;
  }
  if (btn) { btn.disabled = false; btn.textContent = 'Verifizieren'; }
}

/**
 * S259 §7 — Rückfrage vor dem KONTO-Logout.
 *
 * Der Knopf steht in der Topbar direkt neben dem Operator-Chip, und dort liest
 * man „Abmelden" als Operator-Abmeldung. Im S258-Retest hat genau das eine
 * Cowork-Sitzung den Login gekostet: der Magic-Link-Login lässt sich nicht mal
 * eben wiederherstellen. Die Unterscheidung steht jetzt in der Beschriftung
 * („Konto abmelden" vs. „Operator wechseln"), und die Rückfrage fängt den Rest.
 *
 * Kein natives `confirm` (Memory `feedback_gg_inline_over_native_dialogs`);
 * fehlt der Dialog, wird abgemeldet statt blockiert — sonst käme jemand nicht
 * mehr raus.
 */
export async function confirmAccountLogout() {
  if (typeof window !== 'undefined' && typeof window.styledConfirm === 'function') {
    const res = await window.styledConfirm({
      title: 'Vom Konto abmelden?',
      body: 'Das meldet dich vollständig ab — nicht nur den Operator. '
          + 'Für den nächsten Login brauchst du einen neuen Magic-Link oder Code.\n\n'
          + 'Wolltest du den Operator wechseln, ist das der Knopf mit den Initialen daneben.',
      danger: true,
      confirmLabel: 'Konto abmelden',
      cancelLabel: 'Angemeldet bleiben'
    });
    if (!res || !res.confirmed) return;
  }
  await doLogout();
}

export async function doLogout() {
  if (!window.App || !window.App.auth) return;
  await window.App.auth.logout();
  if (window.location.hash === '#admin') {
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  const info = document.getElementById('admin-login-info');
  if (info) { info.style.display = 'none'; info.innerHTML = ''; }
  if (typeof window.switchView === 'function') window.switchView('customer');
  await renderAuthPill();
  showToast('Abgemeldet', 'success');
}

export async function renderAuthPill() {
  const pill = document.getElementById('auth-pill');
  const navAdminBtn = document.getElementById('nav-admin');
  if (!pill) return;
  const App = window.App;
  const isAuthed = App && App.auth && App.auth.isAuthenticated && App.auth.isAuthenticated();
  if (!isAuthed) {
    pill.className = 'auth-pill';
    pill.innerHTML = '';
    if (navAdminBtn) navAdminBtn.textContent = 'Admin';
    return;
  }
  let user = null;
  try { user = await App.auth.getCurrentUser(); } catch (_e) { /* ignore */ }
  if (!user) {
    pill.className = 'auth-pill';
    pill.innerHTML = '';
    if (navAdminBtn) navAdminBtn.textContent = 'Admin';
    return;
  }
  const email = htmlEscape(user.email || '');
  const shortRole = _buildShortRoleLabel(user.role);
  pill.className = 'auth-pill is-authenticated';
  pill.innerHTML = `
    <span class="auth-pill-email" title="Eingeloggt als ${email} (${htmlEscape(shortRole)})">
      👤 ${email}
    </span>
    <button class="auth-pill-logout" onclick="confirmAccountLogout()"
            title="Konto abmelden (nicht der Operator)" aria-label="Konto abmelden">×</button>
  `;
  if (navAdminBtn) navAdminBtn.textContent = 'Admin-Dashboard';
}

// ---------------------------------------------------------------
// Pure helpers (extrahiert in Session 39 für Test-Coverage)
// ---------------------------------------------------------------

// S131 Clean-Cut: service (Kellner) + staff (Küche/Theke/Lieferung), host raus.
const _ROLE_LABEL_MAP = Object.freeze({
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  service: 'Service',
  staff: 'Staff',
});

function _buildShortRoleLabel(role) {
  if (role == null) return '';
  const key = String(role);
  if (Object.prototype.hasOwnProperty.call(_ROLE_LABEL_MAP, key)) {
    return _ROLE_LABEL_MAP[key];
  }
  return key;
}

function _buildMagicLinkInfoHtml(email) {
  const safe = htmlEscape(String(email || '').trim());
  // S61 Stage 2 — Hybrid-Login: 6- bis 8-stelliger Code (robust gegen
  // Mail-Scanner + Cross-Device + bleibt im selben Browser-Fenster) ODER
  // Magic-Link-Klick als Fallback.
  // S144: Code-Variante ist jetzt der prominente DEFAULT (oben, großer Input),
  // der Klick-Link nur noch der dezente Fallback darunter. Grund: der Klick
  // öffnet oft ein neues Browser-Fenster und kann durch Mail-Scanner verbrannt
  // werden; der Code ist der zuverlässige Weg (Jonas-Direktive S144).
  return (
    `<div style="font-size:0.95rem; margin-bottom:6px;">📬 <strong>Login-Mail gesendet</strong></div>` +
    `<div style="color:var(--ink-soft); font-size:0.88rem; margin-bottom:18px;">` +
      `Bitte prüfe das Postfach von <code>${safe}</code>.` +
    `</div>` +

    // === Empfohlen — Code (prominent, großer zentrierter Input) ===
    `<div style="border:2px solid #d97a2c; border-radius:8px; padding:16px 18px 18px; margin-bottom:14px; background:var(--bg-elev, #18201c); color:var(--ink, #e8efe8);">` +
      `<div style="font-weight:600; margin-bottom:4px;">⌨️ Code eingeben <span style="font-weight:600; color:#d97a2c;">— empfohlen</span></div>` +
      `<div style="color:var(--ink-soft); font-size:0.85rem; margin-bottom:10px;">` +
        `Gib den 6- bis 8-stelligen Login-Code aus der Mail ein — funktioniert ` +
        `zuverlässig im selben Fenster, auch bei Cross-Device oder strengen Mail-Filtern:` +
      `</div>` +
      `<form onsubmit="doVerifyOtpCode(event)" style="display:flex; flex-direction:column; gap:10px; align-items:stretch;">` +
        `<input type="text" id="admin-otp-code" inputmode="numeric" pattern="\\d{6,8}" maxlength="8" autocomplete="one-time-code" ` +
              `placeholder="12345678" required ` +
              `style="width:100%; box-sizing:border-box; font-size:1.6rem; letter-spacing:0.4em; text-align:center; padding:12px 14px; border:1px solid var(--border,#ccc); border-radius:6px; font-family:'SFMono-Regular', Menlo, Consolas, monospace; background:var(--bg-soft, #1f2823); color:var(--ink, #e8efe8);">` +
        `<button type="submit" class="btn btn-primary" style="width:100%; padding:11px 14px; font-weight:600;">Verifizieren →</button>` +
      `</form>` +
    `</div>` +

    // === Fallback — Klick ===
    `<div style="border:1px solid var(--border,#e3ddd0); border-radius:8px; padding:14px 16px; margin-bottom:14px; background:var(--bg-elev, #18201c); color:var(--ink, #e8efe8);">` +
      `<div style="font-weight:600; margin-bottom:4px;">🖱️ Alternativ — Klick</div>` +
      `<div style="color:var(--ink-soft); font-size:0.85rem; line-height:1.45;">` +
        `Auf den „<strong>Jetzt einloggen →</strong>"-Button in der Mail klicken. ` +
        `Funktioniert, wenn Mail und Browser auf demselben Gerät geöffnet sind — ` +
        `kann aber ein neues Fenster öffnen.` +
      `</div>` +
    `</div>` +

    `<div style="color:var(--ink-soft); font-size:0.78rem; line-height:1.5;">` +
      `Hinweis: Falls keine Mail ankommt, prüfe den <strong>Spam-Ordner</strong> oder ob die Adresse als Owner/Staff hinterlegt ist. ` +
      `Der Code ist eine Stunde lang gültig.` +
    `</div>`
  );
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, { doLogin, doVerifyOtpCode, doLogout, confirmAccountLogout, renderAuthPill });
}

// ---------------------------------------------------------------
// Test-Hook-Exports (Session 39, Pattern aus customer-form.js /
// menu-picker.js / takeaway-form.js). Zur Laufzeit No-Op.
// ---------------------------------------------------------------
// Hinweis: doVerifyOtpCode ist bereits oben als `export async function`
// deklariert — KEIN doppelter Export hier (Duplicate-Export-SyntaxError, S61).
export {
  _buildShortRoleLabel,
  _buildMagicLinkInfoHtml,
};
